import React, { useEffect, useMemo } from 'react';
import { Card, Box, Typography, Avatar, Badge, Stack, Skeleton } from '@mui/material';
import { getMilestoneAchiever } from '../../../../../utils/constants/pointColors';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getMilestoneAchievers } from '../../../../../redux/features/poinTrackerSlice';

export const TopAchieversCard = ({ fromDate, toDate, selectedTeam }) => {
  const dispatch = useDispatch();
  const { loading, responseData } = useSelector((state) => state.pointTracker.milestoneAchievers);

  const dispatchParams = useMemo(() => {
    return {
      fromDate,
      toDate,
      wingId: selectedTeam?.id === -1 ? null : selectedTeam?.id,
    };
  }, [fromDate, toDate, selectedTeam?.id]);

  useEffect(() => {
    if (fromDate && toDate) {
      dispatch(getMilestoneAchievers(dispatchParams));
    }
  }, [dispatch, dispatchParams]);

  // ✅ Prepare Top 3 Achievers (sorted)
  const achievers = useMemo(() => {
    const data = responseData?.data?.currentPeriod || [];
    // 👉 change to lastMonth if needed

    return [...data]
      .sort((a, b) => b.admissions - a.admissions) // high to low
      .slice(0, 3) // top 3
      .map((item, index) => {
        const sizeMap = [100, 80, 70]; // Gold, Silver, Bronze sizes
        return {
          rank: index + 1,
          name: item.name,
          admission: item.admissions,
          size: sizeMap[index],
        };
      });
  }, [responseData]);

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: 4,
        boxShadow: 3,
        textAlign: 'center',
        maxWidth: 450,
      }}
    >
      {/* Title */}
      {loading ? (
        <Skeleton width="50%" height={25} sx={{ mb: 4 }} />
      ) : (
        <Typography variant="h6" fontWeight="bold" textAlign="left" mb={4}>
          Top Milestone Achievers
        </Typography>
      )}

      {/* Content */}
      <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="center">
        {loading ? (
          [...Array(3)].map((_, index) => (
            <Box key={index} sx={{ textAlign: 'center' }}>
              <Skeleton variant="circular" width={80} height={80} />
              <Skeleton width={80} sx={{ mt: 2, mx: 'auto' }} />
              <Skeleton width={60} sx={{ mx: 'auto' }} />
            </Box>
          ))
        ) : achievers.length === 0 ? (
          <Typography color="text.secondary">No data available</Typography>
        ) : (
          achievers.map((person) => {
            const { color } = getMilestoneAchiever(person.rank);

            return (
              <Box key={person.rank} sx={{ textAlign: 'center' }}>
                {/* Avatar + Rank */}
                <Badge
                  overlap="circular"
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  badgeContent={
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: color,
                        fontSize: 12,
                        border: '2px solid white',
                      }}
                    >
                      {person.rank}
                    </Avatar>
                  }
                >
                  <Avatar
                    sx={{
                      width: person.size,
                      height: person.size,
                      background: 'linear-gradient(135deg, #6366f1, #a855f7)',
                      boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                    }}
                  />
                </Badge>

                {/* Name */}
                <Typography variant="subtitle2" fontWeight="bold" mt={2}>
                  {person.name}
                </Typography>

                {/* admission */}
                <Typography variant="caption" color="text.secondary">
                  {person.admission} Admissions
                </Typography>
              </Box>
            );
          })
        )}
      </Stack>
    </Card>
  );
};
